// QA Workspace — interactive test checklist + reference IA aside.
// Design ported 1:1 from the Claude-design handoff
// (Downloads/shipping-service-qa-page/project/qa-impact-analysis.jsx).
// Replaces the design's localStorage state with SS-backed persistence via
// the new /tickets/:key/test-session endpoint. Evidence uploads route
// through the existing Asset infrastructure (api.presignAsset → S3 PUT →
// api.createAsset) so we don't reinvent the upload pipeline.
//
// Behavior additions on top of the design (the design didn't include these):
//   - "Send to agent" button on Failed-with-observation items (POSTs the
//     readyForDev flag; orchestrator dispatches the bug-fix sandbox).
//   - Empty-IA 404 ⇒ inline prompt to generate the IA first (the workspace
//     is meaningless without the seed checklist).

import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import api from "../../api";
import useBssPoll from "../../hooks/useBssPoll";
import Icon from "./icons";
import { AutoVerdict, EvidenceVideo } from "./BrowserVerifyEvidence";
import Shell from "./Shell";
import {
  apiErrorMessage,
  Modal,
  RefreshButton,
  relativeTime,
  Spinner
} from "./shared";
import "./qa-workspace.css";

/* ---------------------------------------------------------------- constants */
// FEATURE FLAG: the "Send to agent" button persists `readyForDev: true` on
// the item but the orchestrator-side bug-fix sandbox dispatch isn't wired
// yet (see SS QA-WS-7, deferred). Hiding the button until the loop is real
// — clicking it today would look like nothing happened. Flip to true once
// `analyzeImpactForTicket`-style scheduleBugFixForTicket lands.
const BUG_FIX_ENABLED = false;

const STATUSES = [
  { key: "pass", label: "Pass", icon: Icon.Check },
  { key: "fail", label: "Fail", icon: Icon.X },
  { key: "block", label: "Blocked", icon: Icon.AlertTriangle },
  // Verified in-app, but a side effect (email/SMS) needs a human.
  { key: "review", label: "Needs review", icon: Icon.Eye },
  { key: "skip", label: "Skip", icon: Icon.ChevronRight }
];

const FILTERS = [
  { key: "all", label: "All" },
  { key: "untested", label: "Untested" },
  { key: "pass", label: "Pass" },
  { key: "fail", label: "Fail" },
  { key: "block", label: "Blocked" },
  { key: "review", label: "Needs review" },
  { key: "retest", label: "Retest" }
];

// Sort options. "Suggested" = the IA-generated order (no re-sort) — the
// agent already orders roughly by risk/priority within categories, so it's
// a sensible default. Other sorts are pure FE re-orderings of the same
// item set; they never refetch.
//
// Sort applies BEFORE filter, but since filter preserves order either path
// is equivalent. The test-number badge (`n` in TestItem) is derived from
// the ORIGINAL items array index, so numbering stays stable across sorts —
// "test #11" always refers to the same case regardless of view.
const SORT_OPTIONS = [
  { key: "suggested", label: "Suggested order" },
  { key: "priority", label: "Priority (P0 first)" },
  { key: "critical", label: "Critical first" },
  { key: "category", label: "Category (A→Z)" },
  { key: "status", label: "Status (needs-attn first)" }
];

const PRIORITY_RANK = { P0: 0, P1: 1, P2: 2 };
// Status ordering for the "Status" sort. Optimized for QA workflow: pull
// things that need attention to the top (fail → block → untested), then
// completed work (pass → skip) at the bottom so the eye lands on what's
// actionable. "untested" is the bucket key we use elsewhere for null.
const STATUS_RANK = {
  fail: 0,
  review: 1,
  block: 2,
  untested: 3,
  pass: 4,
  skip: 5
};
const statusRank = (s) => STATUS_RANK[s || "untested"] ?? 9;

// Pure module-level sort — moved out of the component so it isn't
// recreated each render (per the React hooks ADR in admin-web's repo).
// Stable: we never mutate the input array; .slice() preserves IA order
// as the implicit tiebreaker via Array.prototype.sort being stable in V8.
function sortItems(items, sort) {
  if (sort === "suggested") return items;
  const arr = items.slice();
  if (sort === "priority") {
    return arr.sort(
      (a, b) =>
        (PRIORITY_RANK[a.priority] ?? 99) - (PRIORITY_RANK[b.priority] ?? 99)
    );
  }
  if (sort === "critical") {
    return arr.sort((a, b) => {
      if (!!a.critical !== !!b.critical) return a.critical ? -1 : 1;
      return (
        (PRIORITY_RANK[a.priority] ?? 99) - (PRIORITY_RANK[b.priority] ?? 99)
      );
    });
  }
  if (sort === "category") {
    return arr.sort((a, b) =>
      (a.category || "").localeCompare(b.category || "")
    );
  }
  if (sort === "status") {
    return arr.sort((a, b) => statusRank(a.status) - statusRank(b.status));
  }
  return arr;
}

const RISK_CLASS = {
  LOW: "qa-risk--low",
  MEDIUM: "qa-risk--medium",
  HIGH: "qa-risk--high"
};

const ALLOWED_EVIDENCE_EXT = [
  "png",
  "jpg",
  "jpeg",
  "pdf",
  "gif",
  "webp",
  "log",
  "txt"
];
const MAX_EVIDENCE_BYTES = 10 * 1024 * 1024; // 10 MB

/* ---------------------------------------------------------------- rich-field utils */

// Try parse + pretty-print. Returns formatted string on success / null on
// failure. Rejects primitives — we only want to render object/array JSON.
function tryPrettyParse(candidate) {
  try {
    const parsed = JSON.parse(candidate);
    if (typeof parsed === "object" && parsed !== null) {
      return JSON.stringify(parsed, null, 2);
    }
  } catch {
    /* not JSON */
  }
  return null;
}

// Walk from `start` (which sits on `open`) until the matching close
// bracket. Returns the end index (inclusive) of the balanced block, or
// -1 if unbalanced. Tracks string state so a brace inside a "…" literal
// doesn't break balance.
function findBalancedEnd(text, start, open, close) {
  let depth = 0;
  let inStr = false;
  let escape = false;
  for (let j = start; j < text.length; j++) {
    const ch = text[j];
    if (inStr) {
      if (escape) escape = false;
      else if (ch === "\\") escape = true;
      else if (ch === '"') inStr = false;
      continue;
    }
    if (ch === '"') inStr = true;
    else if (ch === open) depth += 1;
    else if (ch === close && --depth === 0) return j;
  }
  return -1;
}

// Inline struct-literal blob detector. The IA agent likes to drop config
// snapshots into prose preconditions in a pseudo-JSON shape that uses `=`
// instead of `:` and bare identifiers instead of quoted keys — e.g.
// `guided_scheduling_setting{intelligent_scheduling_enabled=true,
// duration_minutes=30, drive_time_buffer_percent=20}`. Visually this drowns
// in the surrounding sentence. We detect the blob, swap it for an inline
// mono chip rendered out of band so the eye can skip past it cleanly.
//
// Regex is intentionally simple — match any non-nested {…} on one line —
// because a stricter pattern hits ReDoS territory (lazy quantifiers +
// alternation). Validation that the contents actually look like a struct
// is a separate linear pass via `looksLikeStruct` below.
const INLINE_BRACE_BLOB_RE = /\{([^{}\n]{2,})\}/g;
const STRUCT_PAIR_HEAD_RE = /^\s*[A-Za-z_][\w.]*\s*=/;

// Quick "is this a struct literal?" check on the captured brace contents.
// Requires every comma-separated chunk to start with `identifier=`. Linear
// in input length; no backtracking concerns.
function looksLikeStruct(inner) {
  if (!inner.includes("=")) return false;
  const chunks = inner.split(",");
  for (const chunk of chunks) {
    if (!STRUCT_PAIR_HEAD_RE.test(chunk)) return false;
  }
  return true;
}

// Render a single key=value pair from a struct blob. Keys get a color
// accent + `=` is cosmetically replaced with `:` so the chip reads more
// like JSON to the eye (the underlying text in screenshots / copy-paste
// is unaffected — this is a render-only transform).
function renderStructPair(pair, j, last) {
  const eq = pair.indexOf("=");
  const k = eq > 0 ? pair.slice(0, eq).trim() : pair;
  const v = eq > 0 ? pair.slice(eq + 1).trim() : "";
  return (
    <span key={j} className="qa-struct-pair">
      <span className="qa-struct-k">{k}</span>
      {eq > 0 && (
        <>
          <span className="qa-struct-eq">:</span>
          <span className="qa-struct-v"> {v}</span>
        </>
      )}
      {!last && <span className="qa-struct-sep">,</span>}
    </span>
  );
}

// Render free-text prose that may contain inline struct blobs. Returns an
// array of React children — strings and <code> chips interleaved — suitable
// to drop straight into JSX. When no blobs match, returns the original
// string unchanged so React can short-circuit reconciliation.
function renderRichText(text) {
  if (typeof text !== "string" || !text.includes("{")) return text;
  INLINE_BRACE_BLOB_RE.lastIndex = 0;
  const parts = [];
  let lastIndex = 0;
  let chipCount = 0;
  let match = INLINE_BRACE_BLOB_RE.exec(text);
  while (match !== null) {
    if (!looksLikeStruct(match[1])) {
      match = INLINE_BRACE_BLOB_RE.exec(text);
      continue;
    }
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const pairs = match[1]
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    parts.push(
      <code key={`s${chipCount}`} className="qa-inline-struct">
        {pairs.map((p, j) => renderStructPair(p, j, j === pairs.length - 1))}
      </code>
    );
    lastIndex = match.index + match[0].length;
    chipCount += 1;
    match = INLINE_BRACE_BLOB_RE.exec(text);
  }
  if (!chipCount) return text;
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

// Try to extract + pretty-print a JSON payload embedded in a free-text Data
// field. Returns the formatted JSON string when found, else null. The IA
// agent sometimes emits Data as a key=val one-liner, sometimes as a JSON
// object/array, and sometimes as prose with a JSON blob inline — handle
// all three by scanning for the first balanced {…} or […] substring and
// trying to parse it.
function extractPrettyJson(text) {
  if (typeof text !== "string" || text.length < 2) return null;
  const trimmed = text.trim();
  // Fast path: whole field IS the JSON. Common case — keep it cheap.
  const whole = tryPrettyParse(trimmed);
  if (whole) return whole;
  // Substring scan: find the first parseable balanced block.
  for (let i = 0; i < trimmed.length; i++) {
    const open = trimmed[i];
    if (open !== "{" && open !== "[") continue;
    const close = open === "{" ? "}" : "]";
    const end = findBalancedEnd(trimmed, i, open, close);
    if (end < 0) continue;
    const pretty = tryPrettyParse(trimmed.slice(i, end + 1));
    if (pretty) return pretty;
  }
  return null;
}

/* ---------------------------------------------------------------- evidence upload */

/**
 * Upload a single evidence file via the existing Asset pipeline (presign →
 * S3 PUT → create Asset row). Returns the created asset id on success;
 * throws on validation or upload failure so the caller can surface a toast.
 *
 * `subject` and `owner` mirror the polymorphic Asset shape — we tag the
 * file with the ticket key + item id so future cleanup can find them.
 */
async function uploadEvidence(file, { ticketKey, itemId }) {
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
  if (!ALLOWED_EVIDENCE_EXT.includes(ext)) {
    throw new Error(
      `Unsupported file type ".${ext}". Allowed: ${ALLOWED_EVIDENCE_EXT.join(", ")}`
    );
  }
  if (file.size > MAX_EVIDENCE_BYTES) {
    throw new Error(
      `File too large (${(file.size / 1024 / 1024).toFixed(1)} MB > 10 MB cap)`
    );
  }
  const queryParams = new URLSearchParams({
    filename: file.name,
    type: file.type || "application/octet-stream"
  }).toString();
  const presignRes = await api.presignAsset(queryParams);
  const { url, headers, public_url: publicUrl } = presignRes.data;
  const putRes = await fetch(url, {
    method: "PUT",
    headers: headers || {
      "Content-Type": file.type || "application/octet-stream"
    },
    body: file
  });
  if (!putRes.ok) {
    throw new Error(`Upload to S3 failed (${putRes.status})`);
  }
  const createRes = await api.createAsset({
    url: publicUrl || url.split("?")[0],
    subject_type: "QA::TestItem",
    subject_id: itemId,
    owner_type: "QA::TestSession",
    owner_id: ticketKey,
    metadata: {
      filename: file.name,
      mime_type: file.type,
      size: file.size
    }
  });
  // api-2's createAsset returns the Asset entity; pull the id.
  return createRes.data?.id ?? createRes.data?.asset?.id;
}

/* ---------------------------------------------------------------- dependency list */

// Small single-list render for the dependency map. Each entry is a prose
// string the agent emitted ("path/to/file (reason)"). We split at the
// FIRST "(" so the bracketed reason is rendered in a muted color — the
// eye lands on the name first, the reason second. Falls back to whole
// string in the primary color when there's no "(...)" suffix.
const DepList = ({ label, items }) => (
  <div className="qa-deps-section">
    <div className="qa-deps-section-label">
      {label} · {items.length}
    </div>
    <ul className="qa-deps-list">
      {items.map((entry, i) => {
        const paren = entry.indexOf("(");
        const head = paren > 0 ? entry.slice(0, paren).trim() : entry;
        const tail = paren > 0 ? entry.slice(paren).trim() : "";
        return (
          <li key={i}>
            <span className="qa-deps-name">{head}</span>
            {tail && <span className="qa-deps-reason"> {tail}</span>}
          </li>
        );
      })}
    </ul>
  </div>
);

/* --------------------------------------------------- browser-verify (auto QA) */

// EvidenceVideo + AutoVerdict moved to BrowserVerifyEvidence.js — they are
// shared with the Run detail page's per-run verdict panel.

/* Tooltip for the "Verify in browser" header button — names the reason it
   is disabled (run already in flight / no IA yet) or, when enabled, what
   clicking it will do. */
function browserVerifyTitle(inFlight, hasAnalysis) {
  if (inFlight) return "A browser verification is already running.";
  if (!hasAnalysis) {
    return "Generate the Impact Analysis first — verification executes its test cases.";
  }
  return "Boot the app + api in a sandbox and execute the P0/P1 test cases in a real browser, with video evidence per case";
}

/* Rich fields (post IA-SMART): preconditions / steps / expected / data.
   Absent on legacy items seeded from string-only testFocus — renders null so
   old analyses keep the simple look. Extracted from TestItem so the item
   component stays under the cognitive-complexity budget. */
const TestItemRichFields = ({ item }) => {
  const hasSteps = Array.isArray(item.steps) && item.steps.length > 0;
  if (!item.preconditions && !hasSteps && !item.expected && !item.data) {
    return null;
  }
  // Pretty-print JSON data when present, otherwise render the raw mono
  // one-liner the agent emitted. <pre> preserves the indentation we just
  // generated; the plain branch keeps the compact inline-block chip look.
  const prettyData = item.data ? extractPrettyJson(item.data) : null;
  return (
    <div className="qa-test-rich">
      {item.preconditions && (
        <div className="qa-test-rich-row">
          <span className="qa-test-rich-label">Preconditions</span>
          <span className="qa-test-rich-val">
            {renderRichText(item.preconditions)}
          </span>
        </div>
      )}
      {hasSteps && (
        <div className="qa-test-rich-row">
          <span className="qa-test-rich-label">Steps</span>
          {/* Custom step list (not <ol>) so we control the numbering and
              indent — the global CRA reset hides default list-style markers,
              which leaves <ol><li> as a mysterious leading indent with
              nothing in it. Each step is a flex row with an explicit "N."
              prefix in a muted color. */}
          <div className="qa-test-rich-steps">
            {item.steps.map((s, i) => (
              <div key={i} className="qa-step">
                <span className="qa-step-n">{i + 1}.</span>
                <span className="qa-step-text">
                  {renderRichText(s.replace(/^\s*\d+[).]\s*/, ""))}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      {item.expected && (
        <div className="qa-test-rich-row">
          <span className="qa-test-rich-label">Expected</span>
          <span className="qa-test-rich-val">
            {renderRichText(item.expected)}
          </span>
        </div>
      )}
      {item.data && (
        <div className="qa-test-rich-row">
          <span className="qa-test-rich-label">Data</span>
          {prettyData ? (
            <pre className="qa-test-rich-val qa-mono qa-json">{prettyData}</pre>
          ) : (
            <span className="qa-test-rich-val qa-mono">{item.data}</span>
          )}
        </div>
      )}
    </div>
  );
};

/* ---------------------------------------------------------------- test item */
// Re-runs browser verification for just this case (boots the live stack +
// films it). Hidden when the page doesn't supply a verify handler.
const VerifyCaseButton = ({ item, busy, canVerify, onVerifyCase }) => {
  if (!onVerifyCase) return null;
  return (
    <button
      type="button"
      className="qa-act"
      onClick={() => onVerifyCase(item.id)}
      disabled={busy || !canVerify}
      title={
        canVerify
          ? "Re-run browser verification for just this case (boots the live stack + films it)"
          : "Generate an Impact Analysis first, or wait for the current verification to finish"
      }
    >
      <Icon.PlayCircle size={14} />
      Verify
    </button>
  );
};

// Imports a single IA case into a pending release flow. `flowState` is the
// page-held per-case state ("adding" | "added" | undefined). "added" covers
// both a fresh import and a re-import of an already-imported case (import is
// create-if-absent), so the button stays terminal once clicked.
const AddToFlowButton = ({ item, busy, flowState, onAddToFlow }) => {
  if (!onAddToFlow) return null;
  const added = flowState === "added";
  return (
    <button
      type="button"
      className={`qa-act${added ? " is-on" : ""}`}
      onClick={() => onAddToFlow(item.id)}
      disabled={busy || flowState === "adding" || added}
      title={
        added
          ? "This case is already a release flow — publish it from Release flows"
          : "Import this case into a pending release flow"
      }
    >
      {added ? (
        <>
          <Icon.Check size={14} /> Added
        </>
      ) : (
        <>
          <Icon.Plus size={14} />{" "}
          {flowState === "adding" ? "Adding…" : "Add to release flow"}
        </>
      )}
    </button>
  );
};

const TestItem = ({
  n,
  item,
  ticketKey,
  onPatch,
  onSend,
  busy,
  execution,
  onVerifyCase,
  canVerify,
  onAddToFlow,
  flowState
}) => {
  const status = item.status || null;
  const serverObservation = item.observation || "";
  const evidenceIds = Array.isArray(item.evidenceAssetIds)
    ? item.evidenceAssetIds
    : [];
  const retest = !!item.retest;
  const readyForDev = !!item.readyForDev;

  // Drawer auto-opens when there's existing content so QA doesn't have to
  // click through to see what's there.
  const [open, setOpen] = useState(!!(serverObservation || evidenceIds.length));
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState(null);

  // Local observation state — every keystroke would otherwise fire a PATCH
  // (DOS-y + flickers the network panel). QA clicks Save explicitly.
  // Sync from server only when user hasn't dirtied the field, so a polled
  // update can't clobber in-progress typing.
  const [localObservation, setLocalObservation] = useState(serverObservation);
  const [observationDirty, setObservationDirty] = useState(false);
  useEffect(() => {
    if (!observationDirty) setLocalObservation(serverObservation);
  }, [serverObservation, observationDirty]);

  // AWAIT the patch + only clear the dirty flag on success — otherwise a
  // network blip / 422 silently drops the user's draft (the next poll
  // re-syncs from server, useEffect overwrites localObservation with the
  // OLD server value, and the typing disappears with no warning). Failure
  // surfaces via the page-level actionErr banner that patchItem already
  // sets; dirty stays true so the Save button stays available.
  const saveObservation = useCallback(async () => {
    if (!observationDirty) return;
    const ok = await onPatch({ observation: localObservation });
    if (ok) setObservationDirty(false);
  }, [observationDirty, localObservation, onPatch]);

  const cancelObservation = () => {
    setLocalObservation(serverObservation);
    setObservationDirty(false);
  };

  const setStatus = (key) => onPatch({ status: status === key ? null : key });
  const toggleRetest = () => onPatch({ retest: !retest });

  const onFileChange = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = ""; // allow re-selecting the same file
      if (!file) return;
      setUploadErr(null);
      setUploading(true);
      try {
        const id = await uploadEvidence(file, { ticketKey, itemId: item.id });
        if (id) onPatch({ evidenceAssetIds: [...evidenceIds, id] });
      } catch (err) {
        setUploadErr(err?.message || "Upload failed");
      } finally {
        setUploading(false);
      }
    },
    [ticketKey, item.id, evidenceIds, onPatch]
  );

  const removeEvidence = (id) =>
    onPatch({ evidenceAssetIds: evidenceIds.filter((x) => x !== id) });

  // For "has content" + "can send" we read the SERVER-side observation,
  // not the local in-progress draft. Unsaved typing shouldn't count as
  // content (badge would flicker) and shouldn't unlock the agent send
  // (the agent would receive the stale server value, not the draft).
  const hasContent = !!serverObservation || evidenceIds.length > 0;
  const canSend = status === "fail" && serverObservation.trim().length > 5;

  return (
    <div className={`qa-test ${status ? "s-" + status : ""}`}>
      <div className="qa-test-main">
        <div className="qa-test-top">
          <div className="qa-test-num">{n}</div>
          <div className="qa-test-body">
            <div className="qa-test-tags">
              <span className="qa-tag">{item.category}</span>
              {item.priority && (
                <span
                  className={`qa-tag qa-tag--prio-${item.priority.toLowerCase()}`}
                >
                  {item.priority}
                </span>
              )}
              {item.critical && (
                <span className="qa-tag qa-tag--critical">
                  <Icon.AlertTriangle size={11} /> Security-critical
                </span>
              )}
              {retest && (
                <span className="qa-tag qa-tag--retest">
                  <Icon.RotateCw size={11} /> Needs retest
                </span>
              )}
              {Array.isArray(item.covers) &&
                item.covers.map((c) => (
                  <span
                    key={c}
                    className="qa-tag qa-tag--covers"
                    title="Coverage reference"
                  >
                    {c}
                  </span>
                ))}
            </div>
            <div className="qa-test-desc">{item.text}</div>

            {/* Always-present so absence reads as "not run", not just hidden.
                The bot's take; the human's status buttons stay authoritative. */}
            <div className="qa-bverify">
              <span className="qa-bverify-label">Browser verification</span>
              {execution ? (
                <AutoVerdict execution={execution} />
              ) : (
                <span className="qa-bverify-na">
                  Not run yet — use Verify on this case to record it.
                </span>
              )}
            </div>

            <TestItemRichFields item={item} />
          </div>
        </div>

        <div className="qa-test-actions">
          <div className="qa-status" role="group" aria-label="Set result">
            {STATUSES.map((s) => (
              <button
                key={s.key}
                className={`b-${s.key} ${status === s.key ? "is-on" : ""}`}
                onClick={() => setStatus(s.key)}
                aria-pressed={status === s.key}
                disabled={busy}
              >
                <s.icon size={14} /> {s.label}
              </button>
            ))}
          </div>

          <div className="qa-act-spacer" />

          {/* Hidden behind BUG_FIX_ENABLED until the orchestrator-side
              dispatch lands — until then this button would set a flag
              the backend ignores, which is worse than no button at all
              (QA would click + think it worked). */}
          {BUG_FIX_ENABLED && status === "fail" && (
            <button
              type="button"
              className={`qa-send ${readyForDev ? "is-sent" : ""}`}
              disabled={!canSend || readyForDev || busy}
              onClick={() => onSend()}
              title={
                readyForDev
                  ? "Already sent to agent"
                  : canSend
                    ? "Send this failure to the agent for an auto-fix attempt"
                    : "Add an observation (≥6 chars) before sending"
              }
            >
              {readyForDev ? (
                <>
                  <Icon.Check size={13} /> Sent to agent
                </>
              ) : (
                <>
                  <Icon.Rocket size={13} /> Send to agent
                </>
              )}
            </button>
          )}

          <button
            className={`qa-retest ${retest ? "is-on" : ""}`}
            onClick={toggleRetest}
            aria-pressed={retest}
            disabled={busy}
          >
            <span className="box">
              <Icon.Check size={11} />
            </span>
            Retest
          </button>

          <button
            className={`qa-act ${open ? "is-on" : ""} ${hasContent ? "has-content" : ""}`}
            onClick={() => setOpen((o) => !o)}
          >
            <Icon.FileText size={14} />
            Notes
            {hasContent && (
              <span className="badge-n">
                {(serverObservation ? 1 : 0) + evidenceIds.length}
              </span>
            )}
          </button>

          <VerifyCaseButton
            item={item}
            busy={busy}
            canVerify={canVerify}
            onVerifyCase={onVerifyCase}
          />

          <AddToFlowButton
            item={item}
            busy={busy}
            flowState={flowState}
            onAddToFlow={onAddToFlow}
          />
        </div>
      </div>

      {open && (
        <div className="qa-drawer">
          <div>
            <div
              className="qa-field-label"
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              <Icon.FileText size={11} /> Observation
              {observationDirty && (
                <span
                  style={{
                    marginLeft: 6,
                    color: "var(--bss-boom-pink, #f06292)",
                    fontWeight: 700
                  }}
                >
                  · unsaved
                </span>
              )}
            </div>
            <textarea
              className="qa-note"
              placeholder="What did you see? Steps, actual vs expected, anomalies…"
              value={localObservation}
              onChange={(e) => {
                setLocalObservation(e.target.value);
                if (!observationDirty) setObservationDirty(true);
              }}
              onKeyDown={(e) => {
                // Cmd/Ctrl+Enter = save. Common power-user reflex; no
                // surprises for non-power users (they'll use the button).
                if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                  e.preventDefault();
                  saveObservation();
                }
              }}
            />
            {observationDirty && (
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button
                  type="button"
                  className="bss-btn bss-btn--primary bss-btn--sm"
                  onClick={saveObservation}
                  disabled={busy}
                >
                  <Icon.Check size={12} /> Save observation
                </button>
                <button
                  type="button"
                  className="bss-btn bss-btn--ghost bss-btn--sm"
                  onClick={cancelObservation}
                  disabled={busy}
                >
                  <Icon.X size={12} /> Discard
                </button>
                <span
                  style={{
                    alignSelf: "center",
                    fontSize: 11,
                    color: "var(--bss-fg-subtle)"
                  }}
                >
                  ⌘↵ to save
                </span>
              </div>
            )}
          </div>
          <div>
            <div className="qa-field-label">
              <Icon.Eye size={11} /> Evidence
            </div>
            <div className="qa-evidence-list">
              {evidenceIds.map((id) => (
                <span key={id} className="qa-evidence">
                  <Icon.FileText size={13} />
                  asset #{id}
                  <button
                    className="rm"
                    onClick={() => removeEvidence(id)}
                    aria-label="Remove evidence"
                  >
                    <Icon.X size={12} />
                  </button>
                </span>
              ))}
              <label
                className="qa-evidence-add"
                style={{ cursor: uploading ? "wait" : "pointer" }}
              >
                <Icon.Plus size={13} />
                {uploading ? "Uploading…" : "Attach screenshot / log"}
                <input
                  type="file"
                  hidden
                  accept={ALLOWED_EVIDENCE_EXT.map((e) => "." + e).join(",")}
                  onChange={onFileChange}
                  disabled={uploading}
                />
              </label>
            </div>
            {uploadErr && (
              <div
                style={{
                  color: "var(--bss-danger, #f44336)",
                  fontSize: 12,
                  marginTop: 6
                }}
              >
                {uploadErr}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ---------------------------------------------------------------- aside */

function normalizeCrossRepo(crossRepo) {
  if (!Array.isArray(crossRepo) || crossRepo.length === 0) return null;
  // Show the first missing entry as the "FE not in scope" warn box.
  // Other entries (ok / warn) are rendered as-is in their own list below.
  const missing = crossRepo.find((r) => r && r.status === "missing");
  if (missing) {
    return {
      repo: missing.repo || "(unspecified)",
      note: missing.note || ""
    };
  }
  return null;
}

// "What changed" lives ABOVE the body as a full-width hero. The aside now
// only renders a Cross-repo gap warning when one is present (a missing
// expected repo deserves visual real-estate). When there's nothing to
// show we return null so the page reads as a single full-width column —
// "Where to test" and "Safe to skip" were dropped (low signal vs. the
// checklist itself; see commit history for the rationale).
const Aside = ({ analysis }) => {
  const cross = normalizeCrossRepo(analysis.crossRepo);
  if (!cross) return null;
  return (
    <aside className="qa-aside">
      <div className="qa-warn">
        <div className="qa-warn-hd">
          <Icon.AlertTriangle size={14} /> Cross-repo gap
        </div>
        <div className="qa-warn-repo">
          <Icon.GitBranch size={13} /> {cross.repo}{" "}
          <span style={{ color: "var(--bss-fg-subtle)", fontWeight: 400 }}>
            · no PR
          </span>
        </div>
        <div className="qa-warn-text">{cross.note}</div>
      </div>
    </aside>
  );
};

/* ---------------------------------------------------------------- page */
export default function QaWorkspacePage() {
  const { key } = useParams();
  const history = useHistory();
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("suggested");
  const [busy, setBusy] = useState(false);
  const [actionErr, setActionErr] = useState(null);
  // Reset-session confirmation. Keep the destructive action gated behind
  // our own styled Modal rather than window.confirm — native confirm()
  // breaks the visual continuity of the page (browser chrome styling)
  // and is unstyleable. The modal also lets us label the action clearly
  // ("Clear review", not "OK") and show a spinner during the in-flight
  // reset call so the user doesn't double-click.
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  // Re-generate-IA confirmation. Same modal pattern — IA is expensive
  // (~$5–15 per call, 30–60s), so we don't fire on first click.
  const [regenConfirmOpen, setRegenConfirmOpen] = useState(false);
  // Set when the server answers the un-forced regen with 409
  // "pr_set_unchanged" — carries { message, lastRunAt }. The modal then
  // swaps its primary action to an explicit "Regenerate anyway" override.
  const [regenUnchanged, setRegenUnchanged] = useState(null);
  // Empty-state generation flow (when no IA exists yet). `generating`
  // stays true between POST-ok and the next successful session GET — the
  // component naturally exits the empty-state branch when polling lands a
  // session. `generateErr` clears on retry.
  const [generating, setGenerating] = useState(false);
  const [generateErr, setGenerateErr] = useState(null);

  const fetchSession = useCallback(
    () => api.bssTestSession(key).then((res) => res.data),
    [key]
  );
  const { data: session, error, loading, refresh } = useBssPoll(fetchSession);

  // Browser-verify state: latest automated verdicts per item + in-flight flag.
  // Polled alongside the session so verdicts/videos appear as a run lands.
  // Fail-soft: an older SS without the endpoint just renders no auto badges.
  const fetchVerify = useCallback(
    () =>
      api
        .bssBrowserVerifyStatus(key)
        .then((res) => res.data)
        .catch(() => null),
    [key]
  );
  const { data: verify, refresh: refreshVerify } = useBssPoll(fetchVerify);
  const [verifyMsg, setVerifyMsg] = useState(null);
  // Per-case release-flow import state, keyed by the case's stableKey
  // ("adding" while the POST is in flight, "added" once it resolves — whether
  // freshly created or already imported, since import is create-if-absent).
  const [flowImportState, setFlowImportState] = useState({});
  // Optimistic in-flight flag for browser-verify, mirroring pendingIaTrigger:
  // the POST returns 202 on ENQUEUE, but SS only reports inFlight once the
  // queued task actually creates its Run row — without the local flag the
  // button re-enables for that whole gap. Cleared when the server confirms
  // in-flight, or by a generous safety timeout (rejected/queued-slow runs).
  const [pendingVerifyTrigger, setPendingVerifyTrigger] = useState(false);
  const serverVerifyInFlight = verify?.inFlight ?? false;
  const verifyInFlight = serverVerifyInFlight || pendingVerifyTrigger;
  const executionsByKey = useMemo(() => {
    const map = new Map();
    (verify?.executions || []).forEach((e) => map.set(e.stableKey, e));
    return map;
  }, [verify]);
  const flowExecution = verify?.flowStableKey
    ? executionsByKey.get(verify.flowStableKey)
    : executionsByKey.get("(flow)");
  // The most-recent recording to surface at the checklist header, so QA can
  // watch it without hunting through runs: the flow video if this run recorded
  // one, else the newest case that has a video (a flow-skipped per-case run has
  // no flow recording).
  const latestRecording = useMemo(() => {
    const withVideo = (verify?.executions || []).filter(
      (e) => e && e.videoFile
    );
    if (withVideo.length === 0) return null;
    const flowKey = verify?.flowStableKey || "(flow)";
    return (
      withVideo.find((e) => e.stableKey === flowKey) ||
      withVideo[withVideo.length - 1]
    );
  }, [verify]);

  // caseIds present ⇒ targeted re-verify of just those cases (flow skipped).
  const triggerBrowserVerify = useCallback(
    async (caseIds) => {
      setVerifyMsg(null);
      const targeted = Array.isArray(caseIds) && caseIds.length > 0;
      try {
        await api.bssBrowserVerifyTrigger(
          key,
          targeted ? { caseIds } : undefined
        );
        setVerifyMsg(
          targeted
            ? "Verifying this case — the stack boots, then the case runs on camera (~a few min)."
            : "Browser verification queued — the stack boots, then each case runs on camera (~15–20 min)."
        );
        setPendingVerifyTrigger(true);
        refreshVerify();
      } catch (err) {
        const data = err?.response?.data;
        if (data?.error === "no_analysis") {
          setVerifyMsg(
            "Generate the Impact Analysis first — verification executes its test cases."
          );
        } else if (data?.error === "disabled") {
          setVerifyMsg(
            "Browser verification is disabled (BROWSER_VERIFY_ENABLED=false)."
          );
        } else if (data?.error === "cooldown") {
          setVerifyMsg(`Cooldown — retry in ${data.retryAfterSec || 60}s.`);
        } else {
          setVerifyMsg(
            apiErrorMessage(err, "Could not start browser verification.")
          );
        }
      }
    },
    [key, refreshVerify]
  );

  // Import a single IA test case into a pending release flow. `stableKey` is
  // the case's id (executions are keyed by it); `key` is the Jira issue key.
  // Import is create-if-absent server-side, so a re-import of an already-
  // imported case resolves to the existing flow — both land on "added".
  const addToReleaseFlow = useCallback(
    async (stableKey) => {
      setVerifyMsg(null);
      setFlowImportState((prev) => ({ ...prev, [stableKey]: "adding" }));
      try {
        const res = await api.bssQaFlowImport({ issueKey: key, stableKey });
        const created = res?.data?.created;
        setFlowImportState((prev) => ({ ...prev, [stableKey]: "added" }));
        setVerifyMsg(
          created
            ? "Added to release flows — set its app + tags under Release flows to publish it."
            : "Already a release flow — manage it under Release flows."
        );
      } catch (err) {
        setFlowImportState((prev) => {
          const next = { ...prev };
          delete next[stableKey];
          return next;
        });
        setVerifyMsg(
          apiErrorMessage(err, "Could not add this case to a release flow.")
        );
      }
    },
    [key]
  );

  const items = useMemo(() => (session?.items ? session.items : []), [session]);
  const analysis = session?.analysis;
  // Reconciliation drift since the last IA generation (Phase 3). `needsRetest`
  // = items whose spec changed and QA must re-verify; `outdated` = items the
  // new generation dropped (tombstoned, results preserved + recoverable).
  const drift = session?.drift ?? { needsRetest: 0, outdated: 0 };
  // SS sets `iaInFlight: true` when a non-terminal impact-analysis run
  // exists for this ticket. Gates BOTH Reset (because the in-flight IA
  // will reseed and overwrite right after the reset, looking broken) and
  // Re-generate (no point queueing a second IA on top of one already
  // running — the 60s cooldown would 429 anyway). `??` falls through to
  // false on legacy responses that don't carry the field.
  const serverIaInFlight = analysis?.iaInFlight ?? false;
  // Optimistic local flag that bridges the ~1 poll cycle between the
  // /impact-analysis POST returning 202 and the next test-session GET
  // landing with iaInFlight=true. Without this the buttons re-enabled
  // for a beat after clicking Run analysis — and a frustrated user
  // could re-click and 429 themselves on the cooldown.
  // Cleared in the useEffect below either when the server confirms
  // in-flight OR after a safety timeout.
  const [pendingIaTrigger, setPendingIaTrigger] = useState(false);
  const iaInFlight = serverIaInFlight || pendingIaTrigger;
  // True while the push-side reconcile for the latest generation is running
  // (the IA run has finished, so iaInFlight is false, but the reworded/retest
  // results aren't in the rows yet). Keeps the auto-refresh alive through that
  // window so the result lands without a manual reload.
  const reconcilePending = analysis?.reconcilePending ?? false;

  const counts = useMemo(() => {
    const c = {
      pass: 0,
      fail: 0,
      block: 0,
      review: 0,
      skip: 0,
      untested: 0,
      retest: 0,
      tested: 0
    };
    items.forEach((it) => {
      if (it.status) c[it.status] = (c[it.status] || 0) + 1;
      else c.untested += 1;
      if (it.retest) c.retest += 1;
    });
    // review is not "tested" — still needs a human.
    c.tested = c.pass + c.fail + c.block + c.skip;
    return c;
  }, [items]);

  const total = items.length;
  const pct = (n) => (total ? `${(n / total) * 100}%` : "0%");

  // Sort first, then filter. Order is preserved through filter, so the
  // visible list ends up in the user-chosen ordering with non-matching
  // items removed. useMemo so we don't re-sort on unrelated re-renders
  // (busy toggles, poll noise) — the per-test PATCHes are frequent.
  const sortedItems = useMemo(() => sortItems(items, sort), [items, sort]);
  const visible = sortedItems.filter((it) => {
    if (filter === "all") return true;
    if (filter === "untested") return !it.status;
    if (filter === "retest") return !!it.retest;
    return it.status === filter;
  });

  const filterCount = (k) => {
    if (k === "all") return total;
    if (k === "untested") return counts.untested;
    if (k === "retest") return counts.retest;
    return counts[k] || 0;
  };

  // PATCH a single item, optimistically. We don't bother with rollback —
  // single-QA-per-ticket means the server's response is authoritative
  // and refresh() pulls the truth on the next poll.
  // Returns true on success / false on failure, so callers that need to
  // confirm the write before clearing their own state (e.g. the textarea's
  // dirty flag in TestItem.saveObservation) can branch on the result
  // without racing the page-level actionErr banner.
  const patchItem = useCallback(
    async (itemId, patch) => {
      setActionErr(null);
      setBusy(true);
      try {
        await api.bssTestSessionPatchItem({ key, itemId, ...patch });
        refresh();
        return true;
      } catch (e) {
        setActionErr(apiErrorMessage(e, "Could not save change."));
        return false;
      } finally {
        setBusy(false);
      }
    },
    [key, refresh]
  );

  const sendToAgent = useCallback(
    (itemId) => patchItem(itemId, { readyForDev: true }),
    [patchItem]
  );

  // Opens the styled-modal confirmation; actual reset runs in `confirmReset`
  // when the user clicks the destructive button inside the modal.
  const openResetConfirm = useCallback(() => setResetConfirmOpen(true), []);
  const openRegenConfirm = useCallback(() => {
    setRegenUnchanged(null); // clear any stale "unchanged" override
    setRegenConfirmOpen(true);
  }, []);

  // Confirmed Re-generate. Fires the same IA endpoint the empty-state
  // "Generate" button uses (POST is force-by-default on the SS side).
  // Sets the optimistic `pendingIaTrigger` flag immediately on 202 so
  // both buttons stay disabled across the gap between the POST returning
  // and the next test-session GET landing with server-confirmed
  // iaInFlight=true. The flag self-clears in the effect below.
  // `force` is false on the first click (lets the server's dedup guard run);
  // the "Regenerate anyway" override re-calls with force=true. A 409
  // "pr_set_unchanged" is NOT an error — it surfaces the inline override in
  // the modal instead of the red banner.
  const confirmRegenerate = useCallback(
    async (force = false) => {
      setBusy(true);
      setActionErr(null);
      try {
        await api.bssImpactAnalysisGenerate(key, { force });
        setPendingIaTrigger(true);
        setRegenConfirmOpen(false);
        setRegenUnchanged(null);
        refresh();
      } catch (e) {
        if (
          e?.response?.status === 409 &&
          e?.response?.data?.error === "pr_set_unchanged"
        ) {
          setRegenUnchanged(e.response.data);
        } else {
          setActionErr(apiErrorMessage(e, "Could not start impact analysis."));
        }
      } finally {
        setBusy(false);
      }
    },
    [key, refresh]
  );

  // Auto-clear the optimistic pending flag. Two clearing paths:
  //   (a) the server confirms iaInFlight=true on the next poll — the
  //       gap is bridged, the local flag is redundant from here on
  //       (serverIaInFlight will hold the disable until completion).
  //   (b) safety timeout — if SS never reports iaInFlight=true (the
  //       run was rejected, finished super-fast, or the poll is
  //       broken), we don't want the buttons locked forever. 30s is
  //       generous given typical poll cadence is 3-5s.
  useEffect(() => {
    if (!pendingIaTrigger) return undefined;
    if (serverIaInFlight) {
      setPendingIaTrigger(false);
      return undefined;
    }
    const t = setTimeout(() => setPendingIaTrigger(false), 30000);
    return () => clearTimeout(t);
  }, [pendingIaTrigger, serverIaInFlight]);

  // Same clearing pattern for the browser-verify optimistic flag. The
  // timeout is longer: the run is queue-dispatched, so the Run row (and
  // with it inFlight=true) can lag the 202 by more than a poll cycle.
  useEffect(() => {
    if (!pendingVerifyTrigger) return undefined;
    if (serverVerifyInFlight) {
      setPendingVerifyTrigger(false);
      return undefined;
    }
    const t = setTimeout(() => setPendingVerifyTrigger(false), 60000);
    return () => clearTimeout(t);
  }, [pendingVerifyTrigger, serverVerifyInFlight]);

  // Auto-refresh while something is in flight. `useBssPoll` does NOT poll on an
  // interval (it's fetch-once + manual refresh), so without this the workspace
  // would never update after an IA generation / reconcile — the result would
  // only appear on a manual reload. Poll every 4s ONLY while:
  //   - an IA run is in flight (iaInFlight, incl. the optimistic trigger), OR
  //   - the push-side reconcile is running (reconcilePending), OR
  //   - we're generating the first IA from the empty state (no session yet).
  // Each of those resolves on its own, so the interval self-stops — no
  // perpetual background polling (the reason the hook stopped polling globally).
  //   - a browser-verify run is in flight (verifyInFlight, incl. the
  //     optimistic trigger) — refreshes BOTH the verify status (verdicts/
  //     videos land without a reload) and the session (the run writes item
  //     statuses/observations back to the worksheet on completion).
  const shouldPoll =
    iaInFlight ||
    reconcilePending ||
    (generating && !session) ||
    verifyInFlight;
  useEffect(() => {
    if (!shouldPoll) return undefined;
    const t = setInterval(() => {
      refresh();
      refreshVerify();
    }, 4000);
    return () => clearInterval(t);
  }, [shouldPoll, refresh, refreshVerify]);

  const confirmReset = useCallback(async () => {
    setBusy(true);
    setActionErr(null);
    try {
      await api.bssTestSessionReset(key);
      setResetConfirmOpen(false);
      refresh();
    } catch (e) {
      // Surface the error on the page-level banner AND keep the modal open
      // so the user can see what went wrong + retry without losing context.
      setActionErr(apiErrorMessage(e, "Could not reset session."));
    } finally {
      setBusy(false);
    }
  }, [key, refresh]);

  // Empty-state generator — fires the IA POST then leans on the existing
  // polling to detect when the session becomes available. We DON'T reset
  // `generating` on POST success — the component will naturally exit the
  // empty-state branch when polling lands a session, and any earlier reset
  // would flash the button briefly back to "Generate" before re-rendering.
  const generateIA = useCallback(async () => {
    setGenerating(true);
    setGenerateErr(null);
    try {
      await api.bssImpactAnalysisGenerate(key);
      refresh();
    } catch (e) {
      setGenerateErr(apiErrorMessage(e, "Could not start impact analysis."));
      setGenerating(false);
    }
  }, [key, refresh]);

  // Initial-load skeleton. Don't block when we have stale data + are
  // re-polling (e.g. after a patch).
  if (loading && !session && !error) {
    return (
      <Shell>
        <div
          style={{
            padding: "60px 32px",
            textAlign: "center",
            color: "var(--bss-fg-muted)"
          }}
        >
          Loading test session…
        </div>
      </Shell>
    );
  }

  // Error path. SS distinguishes three 404 verdicts via the body's `error`:
  //   - "no_analysis": no IA exists yet — show Generate button.
  //   - "ia_failed":   the last IA run failed — show the reason + a Retry
  //                    button (otherwise the "Working on it…" would spin
  //                    forever on a state we know is dead).
  //   - (other 404):   probably a missing api-2 proxy route — treat as
  //                    no_analysis so the user has a path forward.
  // Any non-404 ⇒ generic apiErrorMessage.
  if (error || !session) {
    const status = error?.response?.status;
    const bodyErr = error?.response?.data?.error;
    const bodyMsg = error?.response?.data?.message;
    const noAnalysis = status === 404 && bodyErr !== "ia_failed";
    const iaFailed = status === 404 && bodyErr === "ia_failed";
    const msg = noAnalysis
      ? bodyErr === "no_analysis"
        ? bodyMsg
        : "The QA workspace is seeded from the impact analysis. Generate one to populate the test checklist."
      : iaFailed
        ? bodyMsg
        : apiErrorMessage(error, "Could not load test session.");
    // Surface a known IA-failed state: don't keep showing "Working on it…"
    // — SS has told us the run died. Reset `generating` so the user sees
    // the failure + a Retry affordance instead of an infinite spinner.
    // The page-level `generating` state is only set by an earlier Generate
    // click; if SS has since reported ia_failed, that click is over.
    if (iaFailed && generating) setGenerating(false);

    // The Generate / Retry button is shared between the no-analysis and
    // ia-failed branches — same endpoint, same flow, different framing.
    const canRetryOrGenerate = noAnalysis || iaFailed;
    return (
      <Shell>
        <div className="qa">
          <div
            style={{
              padding: 60,
              maxWidth: 640,
              margin: "0 auto",
              textAlign: "center"
            }}
          >
            {iaFailed ? (
              <Icon.AlertTriangle
                size={32}
                style={{ color: "var(--bss-warning, #ffa726)" }}
              />
            ) : (
              <Icon.TestTube
                size={32}
                style={{ color: "var(--bss-fg-disabled)" }}
              />
            )}
            <h2 style={{ marginTop: 12, color: "var(--bss-fg-strong)" }}>
              {iaFailed
                ? "Impact analysis failed"
                : noAnalysis
                  ? generating
                    ? "Generating impact analysis…"
                    : "Generate an impact analysis first"
                  : "Could not load"}
            </h2>
            <p style={{ color: "var(--bss-fg-muted)", marginTop: 8 }}>
              {iaFailed
                ? msg
                : noAnalysis
                  ? generating
                    ? "Takes 30–60 seconds. The page polls automatically — the workspace will appear once the analysis lands."
                    : "The QA workspace is seeded from the ticket's impact analysis. Generate one to populate the test checklist."
                  : msg}
            </p>
            {canRetryOrGenerate && (
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  justifyContent: "center",
                  marginTop: 18,
                  flexWrap: "wrap"
                }}
              >
                <button
                  type="button"
                  className="bss-btn bss-btn--primary"
                  onClick={generateIA}
                  disabled={generating}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    opacity: generating ? 0.7 : 1
                  }}
                >
                  {generating ? (
                    <Spinner size={11} />
                  ) : (
                    <Icon.BarChart size={13} />
                  )}
                  {generating
                    ? "Working on it…"
                    : iaFailed
                      ? "Retry generation"
                      : "Generate Impact Analysis"}
                </button>
                <button
                  type="button"
                  className="bss-btn bss-btn--ghost"
                  onClick={() =>
                    history.push(`/bss/tickets/${encodeURIComponent(key)}`)
                  }
                >
                  <Icon.ChevronLeft size={13} /> Back to ticket
                </button>
              </div>
            )}
            {!canRetryOrGenerate && (
              <button
                className="bss-btn bss-btn--primary"
                style={{ marginTop: 16 }}
                onClick={() =>
                  history.push(`/bss/tickets/${encodeURIComponent(key)}`)
                }
              >
                <Icon.ChevronLeft size={13} /> Back to ticket
              </button>
            )}
            {generateErr && (
              <div
                style={{
                  color: "var(--bss-danger, #f44336)",
                  fontSize: 12,
                  marginTop: 14
                }}
              >
                {generateErr}
              </div>
            )}
          </div>
        </div>
      </Shell>
    );
  }

  const allTested = counts.tested === total && total > 0;
  // Pre-computed so the regen button's label isn't a nested ternary (sonarjs).
  const regenActionLabel = regenUnchanged
    ? "Regenerate anyway"
    : "Run analysis";
  const risk = (analysis?.risk || "MEDIUM").toUpperCase();
  const riskClass = RISK_CLASS[risk] || RISK_CLASS.MEDIUM;
  const ticketTitle =
    items[0]?.text && analysis?.summary
      ? analysis.summary.split(/[.;:]/)[0].trim().slice(0, 80)
      : "";

  return (
    <Shell>
      <div className="qa">
        <header className="qa-header">
          <div className="qa-header-inner">
            {/* Two-row header: ticket details (back + id) on top so the
                ticket name has the full width to breathe, then the
                progress + actions strip below. Squashing them into one
                row truncated the title and made the actions feel
                cramped against a small progress bar. */}
            <div className="qa-header-row qa-header-row--id">
              {/* Back to the ticket detail page — sits inline at the very
                  left of the sticky header so it's reachable even after
                  scrolling deep into a long checklist. */}
              <button
                type="button"
                className="qa-reset"
                onClick={() =>
                  history.push(`/bss/tickets/${encodeURIComponent(key)}`)
                }
                title="Back to ticket detail"
              >
                <Icon.ChevronLeft size={13} /> Ticket
              </button>
              <div className="qa-id">
                <div className="qa-id-mark">
                  <Icon.TestTube size={18} />
                </div>
                {/* 3-tier hierarchy so the eye lands on the ticket NAME first
                  (it's what QA mostly cares about), with the page label
                  above and the meta dribble (key + generated-when) below.
                  Single-line stacking >> the old comma-separated soup. */}
                <div className="qa-id-text">
                  <div className="qa-id-eyebrow">
                    QA Impact Analysis
                    <span className={`qa-risk ${riskClass}`}>
                      <span className="dot" /> {risk.toLowerCase()} risk
                    </span>
                  </div>
                  {ticketTitle && (
                    <div className="qa-id-name" title={ticketTitle}>
                      {ticketTitle}
                    </div>
                  )}
                  <div className="qa-id-sub">
                    <span className="key">{key}</span>
                    {analysis?.analyzedAt && (
                      <>
                        <span className="sep">•</span>
                        <span>
                          generated {relativeTime(analysis.analyzedAt)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="qa-header-row qa-header-row--actions">
              <div className="qa-progress">
                <div className="qa-tallies">
                  <span
                    className={`qa-tally t-pass ${counts.pass ? "is-active" : ""}`}
                    title="Passed"
                  >
                    <Icon.Check size={13} /> {counts.pass}
                  </span>
                  <span
                    className={`qa-tally t-fail ${counts.fail ? "is-active" : ""}`}
                    title="Failed"
                  >
                    <Icon.X size={13} /> {counts.fail}
                  </span>
                  <span
                    className={`qa-tally t-block ${counts.block ? "is-active" : ""}`}
                    title="Blocked"
                  >
                    <Icon.AlertTriangle size={13} /> {counts.block}
                  </span>
                </div>

                <div className="qa-progress-meta">
                  <div className="qa-progress-count">
                    <b>{counts.tested}</b>
                    <span className="slash"> / {total}</span>
                  </div>
                  <div className="qa-progress-sub">
                    tested · {counts.untested} left
                  </div>
                </div>

                <div
                  className="qa-progress-bar"
                  title={`${counts.pass} pass · ${counts.fail} fail · ${counts.block} blocked · ${counts.skip} skip`}
                >
                  <span
                    className="seg-pass"
                    style={{ width: pct(counts.pass) }}
                  />
                  <span
                    className="seg-fail"
                    style={{ width: pct(counts.fail) }}
                  />
                  <span
                    className="seg-block"
                    style={{ width: pct(counts.block) }}
                  />
                  <span
                    className="seg-skip"
                    style={{ width: pct(counts.skip) }}
                  />
                </div>

                {/* Re-generate IA — re-runs the impact analysis from scratch.
                  Different from Reset: this discards the IA blob the page is
                  built from, the next IA run rebuilds it (new items, new
                  cross-repo / safe-to-skip, new summary). Gated on
                  `iaInFlight` for the same reason as Reset — clicking
                  while one is running would be a no-op (60s cooldown
                  429s) and confuses QA. */}
                <RefreshButton
                  onClick={() => {
                    refresh();
                    refreshVerify();
                  }}
                  loading={loading}
                />
                {/* Browser-QA verification — MANUAL trigger only: the human
                  reviews the generated cases, then puts the agent + camera on
                  them. Requires an analyzed IA (the cases ARE the IA's). */}
                <button
                  className="qa-reset"
                  onClick={triggerBrowserVerify}
                  title={browserVerifyTitle(verifyInFlight, !!analysis)}
                  disabled={busy || verifyInFlight || !analysis}
                >
                  <Icon.PlayCircle size={13} />{" "}
                  {verifyInFlight ? "Verifying…" : "Verify in browser"}
                </button>
                <button
                  className="qa-reset"
                  onClick={openRegenConfirm}
                  title={
                    iaInFlight
                      ? "An impact analysis is already running — wait for it to finish."
                      : "Re-run the impact analysis (rebuilds the test checklist + reference panels)"
                  }
                  disabled={busy || iaInFlight}
                >
                  <Icon.BarChart size={13} /> Re-generate IA
                </button>
                <button
                  className="qa-reset"
                  onClick={openResetConfirm}
                  title={
                    iaInFlight
                      ? "Disabled while an impact analysis is running — wait, then reset."
                      : "Clear this review (statuses / observations / evidence)"
                  }
                  disabled={busy || iaInFlight}
                >
                  <Icon.RotateCw size={13} /> Reset
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Browser-verify status line + the FLOW segment's automated verdict.
            The flow is the ticket's changed user journey (R1) — distinct from
            the per-case verdicts rendered inline on each item below. */}
        {verifyMsg && (
          <div
            className="bss-card"
            style={{
              padding: 10,
              margin: "10px 0",
              color: "var(--bss-fg-muted)",
              fontSize: 13
            }}
          >
            {verifyMsg}
          </div>
        )}
        {flowExecution && (
          <div className="bss-card" style={{ padding: 12, margin: "10px 0" }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 0.4,
                color: "var(--bss-fg-subtle)",
                marginBottom: 4
              }}
            >
              Flow under test — automated verdict
            </div>
            <AutoVerdict execution={flowExecution} />
          </div>
        )}

        {/* "What changed" hero — full-width above the body so the change
            summary reads as the page-level context. The IA-SMART output
            now carries TWO synthesized blocks that go beyond the lead
            paragraph: an `impact` narrative (4 prose sub-blocks) and a
            `dependencies` structured map (4 lists). Both legacy-safe:
            absent on pre-IA-SMART analyses, in which case only the lead
            paragraph renders (no regression vs the original design). */}
        {analysis?.summary && (
          <section className="qa-changed-hero">
            <div className="qa-changed-hero-inner">
              <div className="qa-card">
                <div className="qa-card-hd">
                  <Icon.Info
                    size={14}
                    style={{ color: "var(--bss-boom-pink)" }}
                  />
                  <span className="qa-sec-label">What changed</span>
                </div>
                <div className="qa-card-bd">
                  <p className="qa-summary">{analysis.summary}</p>

                  {/* Impact narrative — answer to "what does this PR mean
                      for QA?" before reading any individual test case. */}
                  {analysis.impact &&
                    (analysis.impact.userFacing ||
                      analysis.impact.dataAndIntegrations ||
                      analysis.impact.regressionSurface ||
                      analysis.impact.riskCallouts) && (
                      <div className="qa-impact-grid">
                        {analysis.impact.userFacing && (
                          <div className="qa-impact-block">
                            <div className="qa-impact-label">
                              <Icon.Eye size={11} /> User-facing
                            </div>
                            <p className="qa-impact-text">
                              {renderRichText(analysis.impact.userFacing)}
                            </p>
                          </div>
                        )}
                        {analysis.impact.dataAndIntegrations && (
                          <div className="qa-impact-block">
                            <div className="qa-impact-label">
                              <Icon.GitBranch size={11} /> Data &amp;
                              integrations
                            </div>
                            <p className="qa-impact-text">
                              {renderRichText(
                                analysis.impact.dataAndIntegrations
                              )}
                            </p>
                          </div>
                        )}
                        {analysis.impact.regressionSurface && (
                          <div className="qa-impact-block">
                            <div className="qa-impact-label">
                              <Icon.Check size={11} /> Regression surface
                            </div>
                            <p className="qa-impact-text">
                              {renderRichText(
                                analysis.impact.regressionSurface
                              )}
                            </p>
                          </div>
                        )}
                        {analysis.impact.riskCallouts && (
                          <div className="qa-impact-block qa-impact-block--risk">
                            <div className="qa-impact-label">
                              <Icon.AlertTriangle size={11} /> Risk callouts
                            </div>
                            <p className="qa-impact-text">
                              {renderRichText(analysis.impact.riskCallouts)}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                  {/* Dependencies — auditable map of concrete things the
                      changed code touches. Each list renders only when
                      non-empty so a partial map doesn't show empty
                      sections. Default-collapsed via <details> to keep
                      the hero compact; reviewers expand when auditing. */}
                  {analysis.dependencies &&
                    (analysis.dependencies.files?.length > 0 ||
                      analysis.dependencies.databases?.length > 0 ||
                      analysis.dependencies.thirdParty?.length > 0 ||
                      analysis.dependencies.siblings?.length > 0) && (
                      <details className="qa-deps">
                        <summary className="qa-deps-summary">
                          <Icon.Hash size={11} /> Dependency map
                          <span className="qa-deps-count">
                            {(analysis.dependencies.files?.length || 0) +
                              (analysis.dependencies.databases?.length || 0) +
                              (analysis.dependencies.thirdParty?.length || 0) +
                              (analysis.dependencies.siblings?.length ||
                                0)}{" "}
                            entries
                          </span>
                        </summary>
                        <div className="qa-deps-grid">
                          {analysis.dependencies.thirdParty?.length > 0 && (
                            <DepList
                              label="Third-party services"
                              items={analysis.dependencies.thirdParty}
                            />
                          )}
                          {analysis.dependencies.databases?.length > 0 && (
                            <DepList
                              label="Databases"
                              items={analysis.dependencies.databases}
                            />
                          )}
                          {analysis.dependencies.siblings?.length > 0 && (
                            <DepList
                              label="Sibling endpoints / pages"
                              items={analysis.dependencies.siblings}
                            />
                          )}
                          {analysis.dependencies.files?.length > 0 && (
                            <DepList
                              label="Files touched outside the diff"
                              items={analysis.dependencies.files}
                            />
                          )}
                        </div>
                      </details>
                    )}
                </div>
              </div>
            </div>
          </section>
        )}

        <div className="qa-body">
          <main>
            {actionErr && (
              <div
                style={{
                  padding: "10px 14px",
                  marginBottom: 16,
                  background: "var(--bss-danger-bg, rgba(244,67,54,0.12))",
                  border: "1px solid var(--bss-danger, #f44336)",
                  borderRadius: 8,
                  color: "var(--bss-danger, #f44336)",
                  fontSize: 13
                }}
              >
                {actionErr}
              </div>
            )}

            {allTested && (
              <div
                className={`qa-done-banner ${counts.fail ? "has-fail" : ""}`}
              >
                {counts.fail ? (
                  <>
                    <Icon.AlertTriangle size={18} />
                    <span>
                      All {total} items reviewed — <b>{counts.fail} failing</b>
                      {counts.block ? `, ${counts.block} blocked` : ""}. Send to
                      agent or log a bug before sign-off.
                    </span>
                  </>
                ) : (
                  <>
                    <Icon.Check size={18} />
                    <span>
                      All {total} items reviewed and <b>no failures</b>
                      {counts.block ? ` (${counts.block} blocked)` : ""}. Ready
                      for sign-off.
                    </span>
                  </>
                )}
              </div>
            )}

            {/* Reconciliation drift banner (Phase 3) — only after a regen left
                items needing re-verification or archived outdated ones. The
                "need re-verification" count is clickable → filters to Retest. */}
            {(drift.needsRetest > 0 || drift.outdated > 0) && (
              <div className="qa-drift-banner">
                <Icon.RotateCw size={15} />
                <span>
                  Since the last regeneration:
                  {drift.needsRetest > 0 && (
                    <>
                      {" "}
                      <button
                        type="button"
                        className="qa-drift-link"
                        onClick={() => setFilter("retest")}
                      >
                        {drift.needsRetest} item
                        {drift.needsRetest === 1 ? "" : "s"} need
                        {drift.needsRetest === 1 ? "s" : ""} re-verification
                      </button>
                    </>
                  )}
                  {drift.needsRetest > 0 && drift.outdated > 0 ? " · " : ""}
                  {drift.outdated > 0 && (
                    <>
                      {drift.outdated} outdated item
                      {drift.outdated === 1 ? "" : "s"} archived{" "}
                      <span className="qa-drift-muted">
                        (results preserved)
                      </span>
                    </>
                  )}
                  .
                </span>
              </div>
            )}

            <div className="qa-checklist-hd">
              <h2>Test checklist</h2>
              <span className="qa-count-chip">{total} items</span>
              <label
                className={`qa-sort ${sort !== "suggested" ? "is-active" : ""}`}
                title="Reorder the checklist (visual only — numbering stays stable)"
              >
                <Icon.Filter size={12} />
                <span className="qa-sort-label">Sort</span>
                <select
                  className="qa-sort-select"
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  aria-label="Sort test checklist"
                >
                  {SORT_OPTIONS.map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.label}
                    </option>
                  ))}
                </select>
                <Icon.ChevronDown size={11} className="qa-sort-caret" />
              </label>
              <div className="qa-filters">
                {FILTERS.map((f) => (
                  <button
                    key={f.key}
                    className={`qa-filter ${filter === f.key ? "is-active" : ""}`}
                    onClick={() => setFilter(f.key)}
                  >
                    {f.label} <span className="n">{filterCount(f.key)}</span>
                  </button>
                ))}
              </div>
            </div>

            {latestRecording?.videoFile && (
              <div
                className="bss-card"
                style={{ padding: 12, margin: "0 0 12px" }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 6,
                    flexWrap: "wrap"
                  }}
                >
                  <Icon.PlayCircle
                    size={14}
                    style={{ color: "var(--bss-phase-reviewing)" }}
                  />
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                      color: "var(--bss-fg-subtle)"
                    }}
                  >
                    Latest recording
                  </span>
                  <span style={{ fontSize: 12, color: "var(--bss-fg-muted)" }}>
                    {latestRecording.stableKey ===
                    (verify?.flowStableKey || "(flow)")
                      ? "full flow"
                      : "test case"}
                    {" · "}
                    {latestRecording.verdict}
                    {latestRecording.executedAt
                      ? ` · ${relativeTime(latestRecording.executedAt)}`
                      : ""}
                  </span>
                </div>
                <EvidenceVideo
                  runId={latestRecording.runId}
                  file={latestRecording.videoFile}
                />
              </div>
            )}

            {visible.length ? (
              <div className="qa-tests">
                {visible.map((it) => (
                  <TestItem
                    key={it.id}
                    n={items.indexOf(it) + 1}
                    item={it}
                    ticketKey={key}
                    busy={busy}
                    execution={executionsByKey.get(it.id)}
                    onPatch={(patch) => patchItem(it.id, patch)}
                    onSend={() => sendToAgent(it.id)}
                    onVerifyCase={(id) => triggerBrowserVerify([id])}
                    canVerify={!!analysis && !verifyInFlight}
                    onAddToFlow={addToReleaseFlow}
                    flowState={flowImportState[it.id]}
                  />
                ))}
              </div>
            ) : (
              <div className="qa-empty">
                <Icon.Check size={28} />
                <div>
                  Nothing in “{FILTERS.find((f) => f.key === filter)?.label}”.
                </div>
              </div>
            )}
          </main>

          {analysis && <Aside analysis={analysis} />}
        </div>
      </div>

      {/* Destructive-action confirmation. Mirrors the GroupDeployModal
          pattern: title in modal header, body explains the consequence in
          plain words, footer carries the dangerous action on the right
          (matching the destructive-button visual weight) and a passive
          Cancel on the left. We don't reset the in-progress reset on
          modal close — `confirmReset` clears `resetConfirmOpen` itself
          on success, and an in-flight `setBusy(true)` disables the
          button so the user can't double-click it. */}
      <Modal
        open={resetConfirmOpen}
        onClose={() => (busy ? null : setResetConfirmOpen(false))}
        title="Clear this review?"
        width={460}
        footer={
          <>
            <button
              type="button"
              className="bss-btn bss-btn--ghost"
              onClick={() => setResetConfirmOpen(false)}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="bss-btn bss-btn--danger"
              onClick={confirmReset}
              disabled={busy}
            >
              {busy ? (
                <>
                  <Spinner /> Clearing…
                </>
              ) : (
                <>
                  <Icon.RotateCw size={13} /> Clear review
                </>
              )}
            </button>
          </>
        }
      >
        <div
          style={{ fontSize: 13.5, lineHeight: 1.55, color: "var(--bss-fg)" }}
        >
          This clears <strong>your QA work</strong> on this checklist —
          pass/fail/blocked statuses, observations, and evidence attachments —
          across every item.
          <div
            style={{
              marginTop: 10,
              fontSize: 12.5,
              color: "var(--bss-fg-muted)",
              lineHeight: 1.5
            }}
          >
            The <strong>test cases themselves stay</strong> (they come from the
            impact analysis and don&apos;t go away on reset). To rebuild the
            test cases from scratch, use <em>Re-generate IA</em> instead.
          </div>
          <div
            style={{
              marginTop: 10,
              fontSize: 12.5,
              color: "var(--bss-fg-muted)"
            }}
          >
            This cannot be undone.
          </div>
        </div>
      </Modal>

      {/* Re-generate IA confirmation. Mirrors the Reset modal pattern but
          the dangerous action is "Run analysis" — costs $5–15 + 30–60s,
          worth gating behind a click. */}
      <Modal
        open={regenConfirmOpen}
        onClose={() => (busy ? null : setRegenConfirmOpen(false))}
        title="Re-generate impact analysis?"
        width={460}
        footer={
          <>
            <button
              type="button"
              className="bss-btn bss-btn--ghost"
              onClick={() => setRegenConfirmOpen(false)}
              disabled={busy}
            >
              Cancel
            </button>
            {/* NOTE: wrap the handler — a bare onClick passes the click event
                as the first arg, which would be a truthy `force`. */}
            <button
              type="button"
              className={`bss-btn ${regenUnchanged ? "bss-btn--danger" : "bss-btn--primary"}`}
              onClick={() => confirmRegenerate(!!regenUnchanged)}
              disabled={busy}
            >
              {busy ? (
                <>
                  <Spinner /> Starting…
                </>
              ) : (
                <>
                  <Icon.BarChart size={13} /> {regenActionLabel}
                </>
              )}
            </button>
          </>
        }
      >
        <div
          style={{ fontSize: 13.5, lineHeight: 1.55, color: "var(--bss-fg)" }}
        >
          {/* Dedup-guard warning — only after the server reports the PR set is
              unchanged. Frames the second click as a deliberate override. */}
          {regenUnchanged && (
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "flex-start",
                marginBottom: 12,
                padding: "9px 11px",
                borderRadius: 8,
                background: "var(--bss-warning-bg, rgba(255,167,38,0.12))",
                border: "1px solid var(--bss-warning, #ffa726)"
              }}
            >
              <Icon.AlertTriangle
                size={14}
                style={{
                  color: "var(--bss-warning, #ffa726)",
                  flexShrink: 0,
                  marginTop: 1
                }}
              />
              <span style={{ fontSize: 12.5, lineHeight: 1.5 }}>
                <strong>PR set unchanged</strong>
                {regenUnchanged.lastRunAt
                  ? ` since the last run (${relativeTime(regenUnchanged.lastRunAt)})`
                  : ""}
                . Regenerating will re-roll the test plan for no new
                information. Continue only if you want a fresh roll.
              </span>
            </div>
          )}
          This re-runs the impact analysis against the ticket&apos;s current PR
          set. The resulting <strong>test checklist may change</strong> — items
          can be added, reworded, or retired. Your recorded work is reconciled
          across the change: it <strong>follows reworded tests</strong>, gets
          flagged <em>needs re-verification</em> when a test materially changed,
          and is <strong>preserved (archived, recoverable)</strong> on tests the
          new analysis retires — nothing is silently lost.
          <div
            style={{
              marginTop: 10,
              fontSize: 12.5,
              color: "var(--bss-fg-muted)",
              lineHeight: 1.5
            }}
          >
            Takes ~30–60 seconds. Costs about $5–15 of LLM time per run, so
            don&apos;t spam-click. Both Reset and Re-generate will be disabled
            while the analysis is in flight.
          </div>
        </div>
      </Modal>
    </Shell>
  );
}
